/**
 * One consistent line-icon family for the whole product.
 *
 * 24×24 grid, 1.6 stroke, round caps/joins, `currentColor`. Every glyph is a
 * hand-built path so there is no icon-font, no external request and no mix of
 * unrelated styles (the previous menu used unicode symbols such as ✦ ◌ ❧).
 * Icons are decorative by default (`aria-hidden`); pass `title` to expose one.
 */
import type { ReactNode, SVGProps } from 'react';

export type IconName =
  | 'arrow-left' | 'arrow-right' | 'arrow-up' | 'arrow-down' | 'arrow-up-right'
  | 'check' | 'close' | 'plus' | 'minus' | 'chevron-down' | 'chevron-left' | 'chevron-right'
  | 'search' | 'filter' | 'trash' | 'clock' | 'star' | 'users' | 'person' | 'link' | 'copy'
  | 'spark' | 'mic' | 'chat' | 'bag' | 'receipt' | 'split' | 'chart' | 'menu' | 'qr' | 'scan'
  | 'play' | 'wave' | 'leaf' | 'flame' | 'timer' | 'wallet' | 'bell' | 'table' | 'sun' | 'moon'
  | 'sunrise' | 'coffee' | 'bread' | 'bowl' | 'fork' | 'pizza' | 'glass' | 'cake' | 'cloche'
  | 'grill' | 'info' | 'warning' | 'refresh' | 'send' | 'heart' | 'phone' | 'door';

const PATHS: Record<IconName, ReactNode> = {
  'arrow-left': <path d="M19 12H5m6-6-6 6 6 6" />,
  'arrow-right': <path d="M5 12h14m-6-6 6 6-6 6" />,
  'arrow-up': <path d="M12 19V5m-6 6 6-6 6 6" />,
  'arrow-down': <path d="M12 5v14m-6-6 6 6 6-6" />,
  'arrow-up-right': <path d="M7 17 17 7M8 7h9v9" />,
  check: <path d="m5 12.5 4.2 4.2L19 7" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  'chevron-down': <path d="m6 9 6 6 6-6" />,
  'chevron-left': <path d="m15 6-6 6 6 6" />,
  'chevron-right': <path d="m9 6 6 6-6 6" />,
  search: <><circle cx="11" cy="11" r="6.5" /><path d="m16 16 4.5 4.5" /></>,
  filter: <path d="M4 6h16M7 12h10M10 18h4" />,
  trash: <path d="M5 7h14M9 7V5h6v2m-8 0 1 12h8l1-12M10.5 11v5m3-5v5" />,
  clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>,
  star: <path d="m12 3.6 2.55 5.3 5.8.8-4.2 4.1 1 5.8L12 16.8 6.85 19.6l1-5.8-4.2-4.1 5.8-.8L12 3.6Z" />,
  users: <><circle cx="9" cy="8.5" r="3.2" /><path d="M3 20v-1.2A5.8 5.8 0 0 1 8.8 13h.4a5.8 5.8 0 0 1 5.8 5.8V20M16 5.6a3.2 3.2 0 0 1 0 5.8M18.5 13.4A5.6 5.6 0 0 1 21.5 19v1" /></>,
  person: <><circle cx="12" cy="8" r="3.8" /><path d="M4.5 20.5v-1.3a6.5 6.5 0 0 1 6.5-6.5h2a6.5 6.5 0 0 1 6.5 6.5v1.3" /></>,
  link: <path d="m10 14 4-4m-5.5 6-1.2 1.2a3.7 3.7 0 0 1-5.3-5.3l3.2-3.2a3.7 3.7 0 0 1 5.3 0m.5 6a3.7 3.7 0 0 0 5.3 0l3.2-3.2a3.7 3.7 0 0 0-5.3-5.3L14.5 8" />,
  copy: <><rect x="8.5" y="8.5" width="11" height="11" rx="2.5" /><path d="M15.5 8.5V6.5A2.5 2.5 0 0 0 13 4H6.5A2.5 2.5 0 0 0 4 6.5V13a2.5 2.5 0 0 0 2.5 2.5h2" /></>,
  spark: <path d="m12 3 2.3 6.2L20.5 12l-6.2 2.8L12 21l-2.3-6.2L3.5 12l6.2-2.8L12 3Z" />,
  mic: <><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3m-3.5 0h7" /></>,
  chat: <path d="M20 12.2c0 3.8-3.6 6.8-8 6.8-1 0-2-.15-2.9-.45L5 20.4l1.2-3.2A6.5 6.5 0 0 1 4 12.2C4 8.4 7.6 5.4 12 5.4s8 3 8 6.8Z" />,
  bag: <path d="M5.5 8h13l1 12h-15l1-12Zm3.5 0V6.2A3 3 0 0 1 12 3.2a3 3 0 0 1 3 3V8" />,
  receipt: <path d="M6 3.5v17l2.7-1.8 3.3 1.8 3.3-1.8L18 20.5v-17l-2.7 1.6L12 3.5 8.7 5.1 6 3.5ZM9.5 9h5m-5 4h5" />,
  split: <path d="M12 3v5m0 0c0 3-6 3-6 7v1.5M12 8c0 3 6 3 6 7v1.5M6 16.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Zm12 0a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z" />,
  chart: <path d="M4 4v16h16M8.5 15.5V12m4.5 3.5V7.5m4.5 8V10" />,
  menu: <><rect x="4.5" y="3.5" width="15" height="17" rx="2.8" /><path d="M8.5 8.5h7M8.5 12h7M8.5 15.5h4" /></>,
  qr: <><rect x="3.5" y="3.5" width="7" height="7" rx="1.6" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.6" /><rect x="3.5" y="13.5" width="7" height="7" rx="1.6" /><path d="M14 14h2.5v2.5H14zm4 0h2.5M14 19h2.5m3 1.5V18" /></>,
  scan: <path d="M4 8V6a2 2 0 0 1 2-2h2m8 0h2a2 2 0 0 1 2 2v2M4 16v2a2 2 0 0 0 2 2h2m8 0h2a2 2 0 0 0 2-2v-2M3.5 12h17" />,
  play: <path d="M8.5 5.7a1 1 0 0 1 1.5-.85l9 6.1a1 1 0 0 1 0 1.65l-9 6.1a1 1 0 0 1-1.5-.85V5.7Z" />,
  wave: <path d="M4 12v0m3.5-3v6M11 5.5v13M14.5 8v8M18 10.5v3M21 12v0" />,
  leaf: <path d="M19.5 4.5C10 4 5 9 5 15a4 4 0 0 0 4 4c6 0 11-5 10.5-14.5ZM5 20l7-7" />,
  flame: <path d="M12 3c.8 3.6 5 5 5 10a5 5 0 0 1-10 0c0-2 .9-3.2 2-4.2.1 1.7.9 2.7 2 2.7-.8-3 .2-5.2 1-8.5Z" />,
  timer: <><circle cx="12" cy="13.5" r="7" /><path d="M12 10v3.5l2.2 1.4M9.5 3h5" /></>,
  wallet: <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H18v3.5M4 7.5V17a2.5 2.5 0 0 0 2.5 2.5H19a1 1 0 0 0 1-1V9.5a1 1 0 0 0-1-1H6.5A2.5 2.5 0 0 1 4 6M16.5 14h.01" />,
  bell: <path d="M6.5 16.5v-5a5.5 5.5 0 0 1 11 0v5l1.5 1.5H5l1.5-1.5ZM10 20.5h4" />,
  table: <path d="M4 9h16M6 9v10m12-10v10M3.5 9 5 5h14l1.5 4" />,
  sun: <><circle cx="12" cy="12" r="3.8" /><path d="M12 3.5v2m0 13v2M3.5 12h2m13 0h2M6 6l1.4 1.4m9.2 9.2L18 18M6 18l1.4-1.4m9.2-9.2L18 6" /></>,
  moon: <path d="M19.5 14.5A8 8 0 0 1 9.5 4.5a8 8 0 1 0 10 10Z" />,
  sunrise: <path d="M3 18.5h18M6.5 18.5a5.5 5.5 0 0 1 11 0M12 5v3.2M4.8 9.3l1.8 1.8m12.6-1.8-1.8 1.8M9 5.8l3-2.8 3 2.8" />,
  coffee: <path d="M5 9.5h11v4.2A5.3 5.3 0 0 1 10.7 19h-.4A5.3 5.3 0 0 1 5 13.7V9.5Zm11 1.2h1.3a2.4 2.4 0 0 1 0 4.8H16M8 3.5v2.2M11 3.5v2.2M14 3.5v2.2" />,
  bread: <path d="M4 14.5c0-4 3.6-7.5 8-7.5s8 3.5 8 7.5v3H4v-3Zm5.5-4.5.8 7m3.7-7 .8 7" />,
  bowl: <path d="M3.5 11.5h17a8.5 8.5 0 0 1-17 0ZM8 7.5c0-1.3 1-1.8 1-3m3 3c0-1.3 1-1.8 1-3m3 3c0-1.3 1-1.8 1-3" />,
  fork: <path d="M7 3.5v6.5M4.8 3.5v4.4a2.2 2.2 0 0 0 4.4 0V3.5M7 10v10.5M17.5 20.5v-17c-2.2 1.2-3.5 3.8-3.5 7.2h3.5" />,
  pizza: <path d="M12 20.5 4.2 6.2a14.5 14.5 0 0 1 15.6 0L12 20.5ZM9.2 9.3h.01m4.4 1.4h.01M11.5 14h.01" />,
  glass: <path d="M6 4h12l-1.6 15.5a1 1 0 0 1-1 .9H8.6a1 1 0 0 1-1-.9L6 4Zm1 5.2h10M14 4l1.8-1.8" />,
  cake: <path d="M4.5 14h15v6h-15v-6Zm1.8 0c0-3 1.8-4.5 5.7-4.5s5.7 1.5 5.7 4.5M12 9.5V6.5M12 4.2v.01" />,
  cloche: <path d="M3 17.5h18M5 17.5a7 7 0 0 1 14 0M12 10.5V8m-1.6 0h3.2M4 20.5h16" />,
  grill: <path d="M5 11.5h14M7 11.5l-1 8m12-8 1 8M9 8c0-1.6 1-2 1-3.5M13 8c0-1.6 1-2 1-3.5" />,
  info: <><circle cx="12" cy="12" r="8.5" /><path d="M12 11v5m0-8v.01" /></>,
  warning: <path d="M12 4 3 19.5h18L12 4Zm0 5.5v4.5m0 2.8v.01" />,
  refresh: <path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3M19.5 4.5v4h-4" />,
  send: <path d="M20.5 12 4 4.8l3.2 7.2L4 19.2 20.5 12ZM7.2 12h6.3" />,
  heart: <path d="M12 20s-7.5-4.4-7.5-10A4.3 4.3 0 0 1 12 7.4 4.3 4.3 0 0 1 19.5 10c0 5.6-7.5 10-7.5 10Z" />,
  phone: <path d="M6.5 3.5h3l1.5 4-2 1.3a11 11 0 0 0 5.2 5.2l1.3-2 4 1.5v3a2 2 0 0 1-2.2 2A15.5 15.5 0 0 1 4.5 5.7a2 2 0 0 1 2-2.2Z" />,
  door: <path d="M6 20.5V4.5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16M4 20.5h16M13 12h.01" />,
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name' | 'title'> {
  name: IconName;
  size?: number;
  /** Stroke width on the 24-unit grid. */
  weight?: number;
  /** Makes the glyph meaningful to assistive tech. */
  title?: string;
}

export function Icon({ name, size = 20, weight = 1.6, title, style, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={weight}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      data-icon={name}
      style={{ flex: 'none', ...style }}
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}

/** Mirrors a directional icon in RTL so "forward" always points the reading way. */
export const ICON_NAMES = Object.keys(PATHS) as IconName[];
